// Careers — apply to join M Media Corp.
// Editorial layout matching Apply.jsx but framed as the in-house team.
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Toaster } from "../components/ui/sonner";
import { Reveal, MaskReveal } from "../components/Reveal";
import { ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  "Editorial Assistant",
  "PR / Publicist",
  "Talent Manager",
  "Producer / Production Coordinator",
  "Designer (Brand / Editorial)",
  "Photographer / Videographer",
  "Social Editor",
  "Copywriter",
  "Account Strategist",
  "Operations · Studio Manager",
  "Engineering / Web",
  "Internship · Editorial Desk",
  "Internship · Production",
  "Open application",
];

const LOCATIONS = ["Newport Beach", "New York", "Los Angeles", "Remote (US)", "Hybrid"];

const empty = {
  name: "", email: "", phone: "", role: "", location: "",
  portfolio_url: "", linkedin: "", experience: "", why_us: "",
};

export default function Careers() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) {
      toast.error("Name, email, and role are required.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/api/careers`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Application received. The desk reviews every name personally.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-0 relative overflow-hidden" data-testid="careers-page">
      <Toaster richColors position="top-right" />

      <section className="container-edge">
        <div className="overline text-ink/60">The House · Careers</div>
        <MaskReveal delay={0.05}>
          <h1 className="font-display uppercase text-6xl md:text-[11vw] leading-[0.82] mt-4 pb-3">
            Work with<br/><span className="serif-italic font-serif normal-case">M Media</span>.
          </h1>
        </MaskReveal>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-3xl text-lg md:text-2xl text-ink/80 font-light leading-snug">
            We hire slowly and personally. Every role at M Media touches the cover story of a woman the world should know — editors, publicists, producers, designers, photographers, strategists. If your taste is editorial and your standards are higher than the brief, we want to meet you.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-ink/15 pt-10">
          {[
            { n: "01", t: "Taste, first", d: "Portfolio over pedigree. We hire for instinct — the kind a search engine cannot teach." },
            { n: "02", t: "Bicoastal, hybrid", d: "Newport Beach atelier, New York bureau, remote-friendly for the right voice." },
            { n: "03", t: "House values", d: "When one woman wins, we all win. We hire people who already live by that." },
          ].map((s) => (
            <div key={s.n}>
              <div className="overline text-ink/50">{s.n}</div>
              <h3 className="font-display uppercase text-4xl mt-2">{s.t}</h3>
              <p className="text-ink/70 mt-3">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {done ? (
        <section className="container-edge mt-20 pb-32">
          <div className="border-t border-b border-ink py-16" data-testid="careers-success">
            <div className="overline text-ink/55">Application — Received</div>
            <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.85] mt-4">
              Thank you.<br/><span className="serif-italic font-serif normal-case">Your name is in.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-ink/75 text-lg leading-relaxed">
              We read every careers application ourselves. If your taste aligns with where we&apos;re going, a member of the desk will reach out personally — typically within four weeks.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/" className="btn-ghost" data-testid="careers-back-home">Return to index →</Link>
              <button onClick={() => { setDone(false); setForm(empty); }} className="btn-ink" data-testid="careers-reset">Submit another</button>
            </div>
          </div>
        </section>
      ) : (
        <form onSubmit={submit} className="container-edge mt-20 pb-32" data-testid="careers-form">
          {/* 01 — You */}
          <Section number="01" title="You" subtitle="Who we'd be welcoming.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <Field label="Full name *">
                <input data-testid="careers-name" className="editorial-input" value={form.name} onChange={(e) => update("name", e.target.value)} required />
              </Field>
              <Field label="Email *">
                <input data-testid="careers-email" type="email" className="editorial-input" value={form.email} onChange={(e) => update("email", e.target.value)} required />
              </Field>
              <Field label="Phone">
                <input data-testid="careers-phone" className="editorial-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </Field>
              <Field label="Location preference">
                <select data-testid="careers-location" className="editorial-input bg-transparent" value={form.location} onChange={(e) => update("location", e.target.value)}>
                  <option value="">Select…</option>
                  {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* 02 — Role */}
          <Section number="02" title="The role" subtitle="Where you'd plug in.">
            <div className="overline text-ink/55 mb-3">Position *</div>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="careers-role-chips">
              {ROLES.map((r) => {
                const active = form.role === r;
                return (
                  <button type="button" key={r} onClick={() => update("role", active ? "" : r)}
                    className={`overline px-4 py-2 border transition-all ${active ? "bg-ink text-cream border-ink" : "bg-transparent text-ink border-ink/30 hover:border-ink"}`}>
                    {r}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <Field label="Portfolio / Website URL">
                <input data-testid="careers-portfolio" className="editorial-input" placeholder="https://" value={form.portfolio_url} onChange={(e) => update("portfolio_url", e.target.value)} />
              </Field>
              <Field label="LinkedIn">
                <input data-testid="careers-linkedin" className="editorial-input" placeholder="https://www.linkedin.com/in/…" value={form.linkedin} onChange={(e) => update("linkedin", e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* 03 — In your words */}
          <Section number="03" title="In your words" subtitle="The parts that don't fit a CV.">
            <Field label="Tell us about your work — the campaigns, names, or moments you'd want us to see.">
              <textarea data-testid="careers-experience" rows={5} className="editorial-input resize-none" value={form.experience} onChange={(e) => update("experience", e.target.value)} placeholder="Links welcome. Brevity respected." />
            </Field>
            <Field label="Why M Media — and why now?">
              <textarea data-testid="careers-why" rows={4} className="editorial-input resize-none" value={form.why_us} onChange={(e) => update("why_us", e.target.value)} placeholder="Specifics over flattery. The taste, the women, the work — what pulled you here." />
            </Field>
          </Section>

          {/* SUBMIT */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-center border-t border-ink/15 pt-10">
            <p className="md:col-span-7 overline text-ink/55 max-w-xl">
              All applications are read by the desk. We never share or sell submissions.
            </p>
            <div className="md:col-span-5 flex md:justify-end">
              <button type="submit" disabled={loading} className="btn-ink text-base px-8 py-4" data-testid="careers-submit">
                {loading ? "Submitting…" : <>Submit application <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-8 border-t border-ink/15 pt-12">
        <div className="md:col-span-4">
          <div className="overline text-ink/55">Section {number}</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.85] mt-3">{title}</h2>
          {subtitle && <p className="serif-italic font-serif text-2xl text-ink/70 mt-3">{subtitle}</p>}
        </div>
        <div className="md:col-span-8 flex flex-col gap-8">{children}</div>
      </div>
    </Reveal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-ink/55">{label}</span>
      {children}
    </label>
  );
}
